<template>
  <div class="w-full min-h-screen" :class="pageStyles['content']">
    <div
      class="flex justify-start flex-wrap lg:flex-nowrap items-center pl-12 scoped-hero-bg gap-8 pt-12"
      :class="pageStyles['header']"
    >
      <div
        class="pr-12 lg:pr-0 flex flex-col justify-end m-0 p-0 w-full lg:w-1/2"
        :class="[pageStyles['text'], isManagerContext ? 'border-l-8 pl-6' : '']"
      >
        <h1 class="w-full text-left">CareerWise Connections</h1>

        <h4 v-if="isManagerContext" class="text-md w-full text-left">
          Connecting your school community to support career choices.
        </h4>
        <h4 v-else class="text-md w-full text-left">
          Welcome to our Connections at {{ settings.profile_title }}. We're
          thrilled to have you join us and support our school. Whether you can
          spare an hour or a day we appreciate your help.
        </h4>
      </div>
      <div class="w-full lg:w-1/2 flex items-center justify-center">
        <img
          src="/img/connections/images/connections-hero.png"
          alt="Connections Hero Icon"
        />
      </div>
    </div>

    <div
      class="w-full bg-truegray-100 flex flex-col md:flex-row justify-start p-6 lg:p-12"
    >
      <div
        v-if="isAdmin && isManagerContext"
        :style="{ height: 'max-content' }"
        class="flex md:block w-full md:w-16 flex-shrink-0"
      >
        <div
          v-for="(tab, i) in tabViews"
          :key="tab.id"
          :class="[
            'flex cursor-pointer items-center justify-center bg-white text-black hover:text-primary hover:opacity-100 h-16 w-16 transform transition-all',
            i > 0
              ? 'rounded-tr-xl md:rounded-tr-none md:rounded-bl-xl'
              : 'rounded-tl-xl',
            {
              'text-primary': isActiveTab(i),
              'opacity-50': !isActiveTab(i),
            },
          ]"
          :title="tab.title"
          @click="routeToView(tab)"
        >
          <span
            :class="!isActiveTab(i) ? 'opacity-50' : 'scale-125'"
            class="transition-all transform"
            v-html="tab.svg"
          ></span>
        </div>
      </div>
      <!-- <keep-alive> -->
      <component :is="activeTab.name" />
      <!-- </keep-alive> -->
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, watch, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from '../../../js/store/index.js'
import FormUpsertConnection from './FormUpsertConnection.vue'
import FormUpsertReport from './FormUpsertReport.vue'
import TabConnections from './TabConnections.vue'
import TabReports from './TabReports.vue'

export default defineComponent({
  name: 'PageConnections',

  components: {
    FormUpsertConnection,
    FormUpsertReport,
    TabConnections,
    TabReports,
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()

    const VIEWS = [
      {
        name: 'TabConnections',
        title: 'Connections',
        context: 'connections',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M15.75 4.5a3 3 0 11.825 2.066l-8.421 4.679a3.002 3.002 0 010 1.51l8.421 4.679a3 3 0 11-.729 1.31l-8.421-4.678a3 3 0 110-4.132l8.421-4.679a3 3 0 01-.096-.755z" clip-rule="evenodd" /></svg>`,
      },
      {
        name: 'TabReports',
        title: 'Reports',
        context: 'reports',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 013.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 013.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 01-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875zM9.75 17.25a.75.75 0 00-1.5 0V18a.75.75 0 001.5 0v-.75zm2.25-3a.75.75 0 01.75.75v3a.75.75 0 01-1.5 0v-3a.75.75 0 01.75-.75zm3.75-1.5a.75.75 0 00-1.5 0V18a.75.75 0 001.5 0v-5.25z" clip-rule="evenodd" /><path d="M14.25 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0016.5 7.5h-1.875a.375.375 0 01-.375-.375V5.25z" /></svg>`,
      },
    ]

    const tabViews = computed(() => {
      return VIEWS.filter((view) => view.svg !== undefined)
    })
    const isAdmin = computed(() => {
      return store.getters['shared/getUserAccess']
    })

    const settings = computed(() => store.getters['shared/getSettings'])
    const isManagerContext = computed(() =>
      store.getters['connections/isManagerContext'](route.fullPath),
    )

    const activeTab = ref(VIEWS[0])

    const isActiveTab = (i) => {
      return activeTab.value === VIEWS[i]
    }

    const pageStyles = computed(() => {
      return {
        header: 'cw-gradient',
        content: 'bg-truegray-50',
        text: 'text-white border-white',
      }
    })

    /**
     * All the Connections routes use this parent PageConnections component,
     * so to display the appropriate (nested) view we need to check against the
     * route name itself.
     */
    const routeToView = async (tab = null) => {
      if (tab) {
        // This block just updates the route (which is watched, then triggering the next block)
        router.push({ path: `/manager/${tab.context}` })
      } else if (route.meta?.context !== activeTab.value?.context) {
        // This block handles actually dealing with the result of updating the route (previous block)!
        await store.dispatch(
          'connections/dispatchInitFilters',
          route.meta?.context,
        )
        // toggle the index between 0 and 1
        const index = 1 - VIEWS.indexOf(activeTab.value)
        activeTab.value = VIEWS[index]
      }
    }

    watch(
      () => route.name,
      () => routeToView(),
    )

    const canAccessBetaFeature = computed(() => {
      return store.getters['careerwise/canAccessBetaFeature']
    })
    onMounted(() => {
      // if (!canAccessBetaFeature.value) {
      //   router.push('/')
      // } else {
      routeToView()
      // }
    })
    // onMounted(() => routeToView())

    return {
      settings,
      tabViews,
      isAdmin,
      activeTab,
      isActiveTab,
      pageStyles,
      routeToView,
      isManagerContext,
      canAccessBetaFeature,
    }
  },
})
</script>

<style scoped>
.scoped-hero-bg {
  background: linear-gradient(
    145deg,
    rgba(101, 31, 255, 1) 0%,
    rgba(101, 31, 255, 1) 40%,
    rgba(255, 71, 120, 1) 100%
  );
}
</style>
